/**
 * Shared file filtering utilities for upload functionality
 */

import toast from 'react-hot-toast';
import { SUPPORTED_UPLOAD_FILE_EXTENSIONS } from '@/constants/supported-file-types';

export const SUPPORTED_FILE_EXTENSIONS: readonly string[] = SUPPORTED_UPLOAD_FILE_EXTENSIONS;

type FileWithRelativePath = File & {
  webkitRelativePath?: string;
};

// System files that should always be filtered out
const SYSTEM_FILES = [
  '.ds_store',           // macOS
  'thumbs.db',           // Windows
  'desktop.ini',         // Windows
  '.localized',          // macOS
  '._.ds_store',         // macOS resource fork
  '__macosx',            // macOS archive metadata
];

/**
 * Checks if a file should be filtered out (excluded) from upload
 * @param file - The file to check
 * @param acceptedTypes - Optional comma-separated list of accepted extensions (e.g., ".pdf,.docx")
 *                        If not provided, uses SUPPORTED_FILE_EXTENSIONS
 */
export function shouldFilterFile(file: File, acceptedTypes?: string): boolean {
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

  // Filter out hidden files (starting with .)
  if (fileName.startsWith('.')) {
    return true;
  }

  // Filter out known system files
  if (SYSTEM_FILES.includes(fileName)) {
    return true;
  }

  // Filter out macOS metadata folders in paths
  const relativePath = (file as FileWithRelativePath).webkitRelativePath;
  if (relativePath?.toLowerCase().includes('__macosx')) {
    return true;
  }

  // Determine which extensions to check against
  const allowedExtensions: readonly string[] = acceptedTypes
    ? acceptedTypes.split(',').map(ext => ext.trim().toLowerCase())
    : SUPPORTED_FILE_EXTENSIONS;

  // Check for wildcard acceptance
  if (allowedExtensions.some(ext => ext === '*' || ext === '*/*')) {
    return false;
  }

  // Check if file extension is supported
  if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
    return true;
  }

  return false;
}

/**
 * Filters an array of files to only include supported file types
 */
export function filterSupportedFiles(files: File[]): File[] {
  return files.filter(file => !shouldFilterFile(file));
}

/**
 * Gets statistics about filtered files
 */
export function getFilterStats(originalCount: number, filteredCount: number) {
  const filtered = originalCount - filteredCount;
  return {
    originalCount,
    filteredCount,
    filtered,
    hasFiltered: filtered > 0,
    allFiltered: filteredCount === 0
  };
}

/**
 * Filters files and shows toast notifications for filtered files
 * Returns the filtered files or null if all files were filtered
 */
export function filterFilesWithToast(files: File[]): File[] | null {
  const validFiles = filterSupportedFiles(files);
  const filterStats = getFilterStats(files.length, validFiles.length);

  if (filterStats.allFiltered) {
    toast.error('No supported files found. Please upload PDF, Word, PowerPoint, text, Excel, image, email, or ZIP files.');
    return null;
  }

  if (filterStats.hasFiltered) {
    toast(`${filterStats.filtered} file${filterStats.filtered > 1 ? 's' : ''} skipped (unsupported format)`, {
      icon: '⚠️',
      duration: 3000,
    });
  }

  return validFiles;
}
